import { type ServiceEnvironment, getServiceUrl, type ServiceUrlType } from './get-url'
import { logger } from './logger'

export type Query = Record<string, string | number | boolean | null | undefined>

export interface FetchFromServiceOptions {
  path: string
  service: ServiceUrlType<ServiceEnvironment>
  env?: ServiceEnvironment
  query?: Query
  requestInit?: RequestInit
}

export interface FetchDistributionEmbedSettingsOptions {
  distributionId?: string | null | undefined
  clientId?: string | null | undefined
  organizationId?: string | null | undefined
  env?: ServiceEnvironment
}

export async function fetchFromService<T> (opts: FetchFromServiceOptions): Promise<T | undefined> {
  logger.debug('Fetching from service', opts)
  const serviceUrl = getServiceUrl(opts.service, opts.env)
  const url = new URL(serviceUrl)

  url.pathname = opts.path

  for (const [key, value] of Object.entries(opts.query ?? {})) {
    if (value == null) continue
    url.searchParams.append(key, value as string)
  }

  const response = await fetch(url, opts.requestInit)

  if (!response.ok) {
    if (response.status === 404) return undefined

    throw new Error(`Fetch from service failed: ${response.status} ${response.statusText}`)
  }

  const json = await response.json()

  return json as T
}

export async function fetchDistributionEmbedSettings<T> (opts: FetchDistributionEmbedSettingsOptions): Promise<{ fallback?: T, settings?: T }> {
  const settings = await fetchFromService<{ embed?: T }>({
    service: 'experience',
    path: '/data/settings',
    query: {
      id: opts.distributionId as string,
      clientId: opts.clientId as string,
      organizationId: opts.organizationId as string
    },
    env: opts.env,
    requestInit: {
      headers: {
        'content-type': 'application/json'
      }
    }
  })

  if ((settings == null) || settings.embed == null) return {}

  if (typeof settings.embed === 'object' && 'fallbackSettings' in settings.embed && 'settings' in settings.embed) {
    return {
      fallback: settings.embed.fallbackSettings as T,
      settings: settings.embed.settings as T
    }
  }

  return {
    settings: settings.embed as T
  }
}
